/**
 * Next / Done for fields whose keyboard has no Return key.
 *
 * Nov 4 2026, Kasandra: "there needs to be an enter button as you're inputting
 * items.. it feels awkward and broken to just tap outside the screen ... this
 * needs to be consistent across the app."
 *
 * WHY THE APP HAS NO DONE BUTTON TODAY. iOS draws a gray bar (‹ › Done) above
 * the keyboard on every focused field. #331 turned it off for the whole
 * webview (`useHideKeyboardAccessory`) so the chat box sits flush on the
 * keyboard like ChatGPT/Claude. Correct for chat, and it stays off. But the
 * iOS NUMBER PAD (inputMode="decimal"/"numeric", type="tel"/"number") has no
 * Return key at all, so with that bar gone the only way out of an amount field
 * was tapping somewhere else. KeyboardNextBar puts Next/Done back for exactly
 * those fields, in our own UI, without touching #331.
 *
 * Text fields are a different case: they HAVE a Return key. Containers that
 * opt in with `data-enter-next` get Return = go to the next field (see
 * KeyboardNextBar's keydown handler). Give those inputs enterKeyHint="next"
 * so the key is labelled, and autoCorrect="off" — a live autocorrect
 * suggestion makes iOS Return accept the suggestion and never deliver Enter
 * (CLAUDE.md, "A correct onKeyDown Enter handler still dies to iOS
 * autocorrect").
 */

/** Keyboards with no Return key on iOS. */
const NO_RETURN_INPUT_MODES = new Set(["decimal", "numeric"]);
const NO_RETURN_TYPES = new Set(["number", "tel"]);

export function isNoReturnKeyField(el: unknown): el is HTMLInputElement {
  if (!(el instanceof HTMLInputElement)) return false;
  if (el.disabled || el.readOnly) return false;
  const mode = (el.getAttribute("inputmode") || "").toLowerCase();
  return NO_RETURN_INPUT_MODES.has(mode) || NO_RETURN_TYPES.has(el.type);
}

/** Inputs Next may land on. Date/time are left out on purpose: focusing one
 *  pops a picker nobody asked for (same rule as textFieldEnterAdvance.ts). */
const NEXTABLE_INPUT_TYPES = new Set(["text", "search", "email", "tel", "url", "number", "password"]);

function isNextable(el: Element): el is HTMLElement {
  if ((el as HTMLInputElement).disabled) return false;
  if (el.closest("[hidden], [aria-hidden='true']")) return false;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly;
  if (el instanceof HTMLSelectElement) return true;
  if (el instanceof HTMLInputElement) return !el.readOnly && NEXTABLE_INPUT_TYPES.has(el.type);
  return false;
}

/** The field Next should go to, or null when `current` is the last one.
 *  Scoped to the open sheet/dialog so Next never jumps into the page behind. */
export function findNextField(current: HTMLElement): HTMLElement | null {
  const scope =
    current.closest<HTMLElement>("[data-kin-sheet-scroll], [role='dialog'], form") ??
    current.ownerDocument.body;
  const fields = Array.from(scope.querySelectorAll("input, textarea, select")).filter(isNextable);
  const i = fields.indexOf(current);
  if (i < 0) return null;
  return fields[i + 1] ?? null;
}

/** Move to the next field, or close the keyboard from the last one.
 *  Inside a sheet the focusin rescue is the only thing allowed to scroll
 *  (single-scroller rule, useKeyboardSafeSheet.ts). */
export function goToNextField(current: HTMLElement): "advanced" | "closed" {
  const next = findNextField(current);
  if (next) {
    // preventScroll ONLY inside a sheet scrollport, where the focusin rescue
    // will reveal the field. Anywhere else (a plain page, Quick Add) nothing
    // would scroll it into view, so let the browser do it (Copilot, #2027).
    next.focus(next.closest("[data-kin-sheet-scroll]") ? { preventScroll: true } : undefined);
    return "advanced";
  }
  current.blur();
  return "closed";
}

/** True when Return in this text field should advance (opted-in container). */
export function wantsEnterNext(el: unknown): el is HTMLInputElement {
  if (!(el instanceof HTMLInputElement)) return false;
  if (!NEXTABLE_INPUT_TYPES.has(el.type)) return false;
  return !!el.closest("[data-enter-next]");
}

/** Spread onto a single-line text input to make Return mean "next field".
 *  All three are needed: the label, the autocorrect fix, the opt-in. */
export const ENTER_NEXT_PROPS = {
  enterKeyHint: "next",
  autoCorrect: "off",
  "data-enter-next": "",
} as const;

/** A screen that pins its own action button on top of the keyboard (the
 *  Party Planner wizard's Next, `data-kin-sheet-footer`) already has a way
 *  forward right where the strip would go. The strip steps aside there rather
 *  than cover it: the strip's Next only changes fields, the footer's saves and
 *  advances the wizard (Copilot, #2027). Only when the footer has content —
 *  the wizard renders an empty footer on its last step. */
export function hasPinnedKeyboardFooter(field: HTMLElement): boolean {
  let el: HTMLElement | null = field.parentElement;
  while (el) {
    for (const child of Array.from(el.children)) {
      if (child.hasAttribute("data-kin-sheet-footer") && child.childElementCount > 0) return true;
    }
    el = el.parentElement;
  }
  return false;
}
